<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import CampaignMediaAPI from 'dashboard/api/campaignMedia';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { useAlert } from 'dashboard/composables';

const router = useRouter();
const ui = {
  back: 'Voltar',
  title: 'Midias de campanhas',
  subtitle: 'Arquivos publicos disponiveis em /campanhas',
  refresh: 'Atualizar',
  upload: 'Enviar arquivo',
  recentProcessing: 'Processamento recente',
  convert: 'Converter MOV para MP4',
  compress: 'Criar versao de ate 16 MB',
  visual: 'Visual',
  file: 'Arquivo',
  type: 'Tipo',
  size: 'Tamanho',
  updated: 'Atualizado',
  actions: 'Acoes',
  save: 'Salvar',
  cancel: 'Cancelar',
  movHint: 'Converta para MP4 para gerar um link publico.',
  preview: 'Visualizar',
  copy: 'Copiar link',
  rename: 'Renomear',
  delete: 'Excluir',
  deleteTitle: 'Excluir arquivo?',
  deleteDescription:
    'Esta acao remove o arquivo permanentemente e o link publico deixa de funcionar.',
  deleteConfirm: 'Excluir arquivo',
  noMedia: 'Nenhuma midia enviada.',
};
const files = ref([]);
const operations = ref([]);
const loading = ref(true);
const uploading = ref(false);
const fileInput = ref(null);
const renamingId = ref(null);
const renameValue = ref('');
const deleteDialog = ref(null);
const deletingFile = ref(false);
const selectedFile = ref(null);
let pollTimer;

const hasActiveOperations = computed(() =>
  operations.value.some(operation =>
    ['queued', 'processing'].includes(operation.status)
  )
);

const formatSize = bytes => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const index = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1
  );
  return `${(bytes / 1024 ** index).toFixed(index ? 1 : 0)} ${units[index]}`;
};

const formatDate = value =>
  new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));

const errorMessage = error =>
  error?.response?.data?.error || 'Nao foi possivel concluir a operacao.';

const fetchFiles = async () => {
  const { data } = await CampaignMediaAPI.get();
  files.value = data.files;
};

const fetchOperations = async () => {
  const { data } = await CampaignMediaAPI.operations();
  const wasActive = hasActiveOperations.value;
  operations.value = data.operations;
  if (wasActive && !hasActiveOperations.value) await fetchFiles();
};

const refresh = async () => {
  loading.value = true;
  try {
    await Promise.all([fetchFiles(), fetchOperations()]);
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    loading.value = false;
  }
};

const upload = async event => {
  const [file] = event.target.files;
  if (!file) return;
  uploading.value = true;
  try {
    await CampaignMediaAPI.upload(file);
    useAlert('Arquivo enviado com sucesso.');
    await fetchFiles();
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    uploading.value = false;
    event.target.value = '';
  }
};

const openFile = file => window.open(file.url, '_blank', 'noopener');

const copyLink = async file => {
  await navigator.clipboard.writeText(file.url);
  useAlert('Link copiado.');
};

const startRename = file => {
  renamingId.value = file.id;
  renameValue.value = file.name;
};

const rename = async file => {
  try {
    await CampaignMediaAPI.rename(file.id, renameValue.value);
    renamingId.value = null;
    useAlert('Arquivo renomeado.');
    await fetchFiles();
  } catch (error) {
    useAlert(errorMessage(error));
  }
};

const openDelete = file => {
  selectedFile.value = file;
  deleteDialog.value?.open();
};

const deleteFile = async () => {
  if (!selectedFile.value) return;

  deletingFile.value = true;
  try {
    await CampaignMediaAPI.delete(selectedFile.value.id);
    useAlert('Arquivo excluido com sucesso.');
    deleteDialog.value?.close();
    selectedFile.value = null;
    await fetchFiles();
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    deletingFile.value = false;
  }
};

const processFile = async (file, kind) => {
  try {
    await CampaignMediaAPI.process(file.id, kind);
    useAlert(
      kind === 'convert'
        ? 'Conversao adicionada a fila.'
        : 'Compactacao adicionada a fila.'
    );
    await fetchOperations();
  } catch (error) {
    useAlert(errorMessage(error));
  }
};

const operationLabel = operation =>
  ({
    queued: 'Na fila',
    processing: 'Processando',
    done: 'Concluida',
    failed: 'Falhou',
  })[operation.status];

onMounted(async () => {
  await refresh();
  pollTimer = setInterval(fetchOperations, 3000);
});
onBeforeUnmount(() => clearInterval(pollTimer));
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header class="px-6 border-b border-n-weak">
      <div
        class="flex items-center justify-between gap-3 h-20 w-full max-w-7xl mx-auto"
      >
        <div class="flex items-center gap-3 min-w-0">
          <Button
            icon="i-lucide-arrow-left"
            variant="ghost"
            color="slate"
            :title="ui.back"
            @click="router.push({ name: 'campaigns_whatsapp_index' })"
          />
          <div>
            <h1 class="text-heading-1 text-n-slate-12">{{ ui.title }}</h1>
            <p class="text-sm text-n-slate-11">
              {{ ui.subtitle }}
            </p>
          </div>
        </div>
        <div class="flex gap-2">
          <Button
            icon="i-lucide-refresh-cw"
            variant="faded"
            color="slate"
            :title="ui.refresh"
            @click="refresh"
          />
          <Button
            :label="ui.upload"
            icon="i-lucide-upload"
            :is-loading="uploading"
            @click="fileInput.click()"
          />
          <input
            ref="fileInput"
            class="hidden"
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,.mov"
            @change="upload"
          />
        </div>
      </div>
    </header>

    <main class="flex-1 overflow-y-auto px-6 py-5">
      <div class="w-full max-w-7xl mx-auto space-y-6">
        <section v-if="operations.length" class="space-y-2">
          <h2 class="text-base font-semibold text-n-slate-12">
            {{ ui.recentProcessing }}
          </h2>
          <div class="flex gap-3 overflow-x-auto pb-2">
            <div
              v-for="operation in operations.slice(0, 8)"
              :key="operation.id"
              class="min-w-64 border border-n-weak rounded-md p-3 bg-n-surface-2"
            >
              <div class="flex justify-between gap-2">
                <span class="text-sm font-medium text-n-slate-12 truncate">{{
                  operation.source_name
                }}</span>
                <span
                  class="text-xs"
                  :class="
                    operation.status === 'failed'
                      ? 'text-n-ruby-11'
                      : operation.status === 'done'
                        ? 'text-n-teal-11'
                        : 'text-n-blue-11'
                  "
                >
                  {{ operationLabel(operation) }}
                </span>
              </div>
              <p class="text-xs text-n-slate-11 mt-1">
                {{ operation.kind === 'convert' ? ui.convert : ui.compress }}
              </p>
              <p
                v-if="operation.target_name"
                class="text-xs text-n-teal-11 mt-1 truncate"
              >
                {{ operation.target_name }}
              </p>
              <p
                v-if="operation.error_message"
                class="text-xs text-n-ruby-11 mt-1 line-clamp-2"
              >
                {{ operation.error_message }}
              </p>
            </div>
          </div>
        </section>

        <div
          class="border border-n-weak rounded-lg overflow-x-auto bg-n-surface-2"
        >
          <div v-if="loading" class="flex justify-center py-16">
            <Spinner />
          </div>
          <table v-else class="w-full min-w-[980px] text-sm">
            <thead class="bg-n-alpha-2 text-left text-xs text-n-slate-11">
              <tr>
                <th class="px-4 py-3 w-24">{{ ui.visual }}</th>
                <th class="px-4 py-3">{{ ui.file }}</th>
                <th class="px-4 py-3">{{ ui.type }}</th>
                <th class="px-4 py-3">{{ ui.size }}</th>
                <th class="px-4 py-3">{{ ui.updated }}</th>
                <th class="px-4 py-3 text-right">{{ ui.actions }}</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <tr
                v-for="file in files"
                :key="file.id"
                class="hover:bg-n-alpha-1"
              >
                <td class="px-4 py-3">
                  <a
                    v-if="file.public"
                    :href="file.url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="block w-16 h-12 rounded overflow-hidden bg-n-alpha-2"
                  >
                    <img
                      v-if="file.kind === 'image'"
                      :src="file.url"
                      :alt="file.name"
                      class="w-full h-full object-cover"
                    />
                    <video
                      v-else
                      :src="file.url"
                      muted
                      preload="metadata"
                      class="w-full h-full object-cover"
                    />
                  </a>
                  <div
                    v-else
                    class="flex items-center justify-center w-16 h-12 rounded bg-n-alpha-2 text-n-slate-10"
                  >
                    <span class="i-lucide-file-video text-xl" />
                  </div>
                </td>
                <td class="px-4 py-3 max-w-80">
                  <div
                    v-if="renamingId === file.id"
                    class="flex items-center gap-2"
                  >
                    <Input
                      v-model="renameValue"
                      size="sm"
                      class="flex-1"
                      @enter="rename(file)"
                    />
                    <Button
                      icon="i-lucide-check"
                      size="sm"
                      :title="ui.save"
                      @click="rename(file)"
                    />
                    <Button
                      icon="i-lucide-x"
                      size="sm"
                      variant="ghost"
                      color="slate"
                      :title="ui.cancel"
                      @click="renamingId = null"
                    />
                  </div>
                  <div v-else>
                    <p class="font-medium text-n-slate-12 break-all">
                      {{ file.name }}
                    </p>
                    <p v-if="!file.public" class="text-xs text-n-amber-11">
                      {{ ui.movHint }}
                    </p>
                  </div>
                </td>
                <td class="px-4 py-3 text-n-slate-11">
                  {{ file.content_type }}
                </td>
                <td class="px-4 py-3 tabular-nums text-n-slate-11">
                  {{ formatSize(file.size) }}
                </td>
                <td class="px-4 py-3 text-n-slate-11">
                  {{ formatDate(file.updated_at) }}
                </td>
                <td class="px-4 py-3">
                  <div class="flex justify-end gap-1">
                    <Button
                      v-if="file.public"
                      icon="i-lucide-external-link"
                      variant="ghost"
                      color="slate"
                      size="sm"
                      :title="ui.preview"
                      @click="openFile(file)"
                    />
                    <Button
                      v-if="file.public"
                      icon="i-lucide-copy"
                      variant="ghost"
                      color="slate"
                      size="sm"
                      :title="ui.copy"
                      @click="copyLink(file)"
                    />
                    <Button
                      icon="i-lucide-pencil"
                      variant="ghost"
                      color="slate"
                      size="sm"
                      :title="ui.rename"
                      @click="startRename(file)"
                    />
                    <Button
                      v-if="file.name.toLowerCase().endsWith('.mov')"
                      icon="i-lucide-file-cog"
                      variant="faded"
                      color="slate"
                      size="sm"
                      :title="ui.convert"
                      @click="processFile(file, 'convert')"
                    />
                    <Button
                      v-if="file.kind === 'video'"
                      icon="i-lucide-minimize-2"
                      variant="faded"
                      color="slate"
                      size="sm"
                      :title="ui.compress"
                      @click="processFile(file, 'compress')"
                    />
                    <Button
                      icon="i-lucide-trash-2"
                      variant="ghost"
                      color="ruby"
                      size="sm"
                      :title="ui.delete"
                      @click="openDelete(file)"
                    />
                  </div>
                </td>
              </tr>
              <tr v-if="!files.length">
                <td colspan="6" class="px-4 py-14 text-center text-n-slate-11">
                  {{ ui.noMedia }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </main>
    <Dialog
      ref="deleteDialog"
      type="alert"
      :title="ui.deleteTitle"
      :description="`${ui.deleteDescription} ${selectedFile?.name || ''}`"
      :confirm-button-label="ui.deleteConfirm"
      :is-loading="deletingFile"
      @confirm="deleteFile"
      @close="selectedFile = null"
    />
  </section>
</template>
